import { defineSlotRecipe } from '@chakra-ui/react/styled-system';

import { type NotificationVariant } from '../common/AlertAndToast.common';

const MESSAGE_ONLY_STATUSES: NotificationVariant[] = ['critical', 'progress', 'success'];

const SIDEBAR_ICON_COLOR_MAP: Record<NotificationVariant, string> = {
  ai: 'color/indigo/base',
  critical: 'color/red/base',
  info: 'color/blue/base',
  progress: 'color/purple/base',
  success: 'color/green/base',
  warning: 'color/yellow/base',
};

const STATUS_TEXT_COLOR_MAP: Record<NotificationVariant, string> = {
  ai: 'status/ai/text',
  critical: 'text/negative',
  info: 'text/primary',
  progress: 'text/secondary',
  success: 'text/positive',
  warning: 'text/primary',
};

const getStatusVariant = (v: NotificationVariant) => ({
  iconBar: {
    backgroundColor: `status/${v}/bg`,
    color: SIDEBAR_ICON_COLOR_MAP[v],
  },
  root: { borderColor: `status/${v}/border` },
  title: { color: STATUS_TEXT_COLOR_MAP[v] },
  ...(MESSAGE_ONLY_STATUSES.includes(v) && {
    message: { '&:only-child': { color: STATUS_TEXT_COLOR_MAP[v] } },
  }),
});

const noteCardSlotRecipe = defineSlotRecipe({
  className: 'note-card',
  slots: [
    'root',
    'iconBar',
    'iconWrapper',
    'content',
    'messageBlock',
    'messageList',
    'title',
    'message',
    'collapsible',
    'collapsibleContent',
    'collapsibleTrigger',
    'actionArea',
    'actionAreaList',
  ],
  base: {
    root: {
      alignItems: 'stretch',
      backgroundColor: 'background/primary',
      border: '1px solid',
      borderRadius: '8',
      display: 'flex',
      overflow: 'hidden',
    },
    iconBar: {
      alignItems: 'flex-start',
      display: 'flex',
      flexShrink: 0,
      justifyContent: 'center',
      paddingBlock: '12',
      paddingInline: '8',
    },
    iconWrapper: {
      alignItems: 'center',
      display: 'flex',
      height: '24',
      justifyContent: 'center',
      width: '24',
    },
    content: {
      alignItems: { base: 'flex-end', tablet: 'center' },
      display: 'flex',
      flex: 1,
      flexDirection: { base: 'column', tablet: 'row' },
      gap: { base: '12', tablet: '24' },
      minWidth: 0,
      paddingBlock: '12',
      paddingInlineEnd: '16',
      paddingInlineStart: '12',
    },
    messageBlock: {
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      gap: '4',
      minWidth: 0,
      width: '100%',
    },
    messageList: {
      paddingBlock: '4',
    },
    title: {
      textStyle: 'body/lg/semibold',
    },
    message: {
      textStyle: 'body/md/regular',
      color: 'text/body',
    },
    collapsible: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4',
      width: '100%',
    },
    collapsibleContent: {
      overflow: 'hidden',
      _open: {
        animationName: 'expand-height, fade-in',
        animationDuration: 'moderate',
      },
      _closed: {
        animationName: 'collapse-height, fade-out',
        animationDuration: 'moderate',
      },
    },
    collapsibleTrigger: {
      alignSelf: 'flex-start',
      marginBlockStart: '4',
    },
    actionArea: {
      flexShrink: 0,
    },
    actionAreaList: {
      alignSelf: { tablet: 'flex-start' },
    },
  },
  variants: {
    status: {
      ai: {
        iconBar: {
          background: 'gradient/ai/background/minimal-vertical',
          color: SIDEBAR_ICON_COLOR_MAP.ai,
        },
        root: {
          backgroundColor: 'transparent',
          border: '1px solid transparent',
          background:
            'linear-gradient({colors.neutral.100}, {colors.neutral.100}) padding-box, linear-gradient(135deg, {colors.purple.80} 0%, {colors.indigo.80} 50%, {colors.blue.80} 100%) border-box',
        },
        title: { color: STATUS_TEXT_COLOR_MAP.ai },
      },
      critical: getStatusVariant('critical'),
      info: getStatusVariant('info'),
      progress: getStatusVariant('progress'),
      success: getStatusVariant('success'),
      warning: getStatusVariant('warning'),
    },
  },
  defaultVariants: {
    status: 'info',
  },
});

export default noteCardSlotRecipe;
